import React, { useEffect, useState } from 'react';
import { Select, Form, Button, Modal, Input, Space, notification, Table, Card } from 'antd';
import { CopyOutlined, MinusCircleOutlined, PlusOutlined, ReloadOutlined } from '@ant-design/icons';
import { JsonView, defaultStyles, darkStyles } from 'react-json-view-lite';
import { useThemeContext } from '../../../app/ThemeProvider';
import _, { omitBy, isNil, toNumber } from 'lodash';
import moment from 'moment';
import { createApiGateway } from '../../../apiGateway';
import { Spinner } from '../../../components';

const encodePath = (path) => JSON.stringify(path);

const decodePath = (pathString) => {
  try {
    return JSON.parse(pathString);
  } catch (e) {
    return [];
  }
};

const formatPathLabel = (path) => {
  if (!path.length) return 'Entire JSON';

  return path
    .map((segment) => (typeof segment === 'number' ? `[${segment}]` : segment))
    .reduce((acc, segment, index) => {
      if (index === 0) return segment;
      if (segment.startsWith('[')) return `${acc}${segment}`;
      return `${acc}.${segment}`;
    }, '');
};

const describeNode = (value) => {
  if (Array.isArray(value)) {
    const count = value.length;
    return `Array, ${count} ${count === 1 ? 'item' : 'items'}`;
  }

  const keysCount = Object.keys(value || {}).length;
  return `Object, ${keysCount} ${keysCount === 1 ? 'key' : 'keys'}`;
};

const buildCopyTargets = (value, path = []) => {
  if (!value || typeof value !== 'object') {
    return [];
  }

  const current = {
    label: `${formatPathLabel(path)} (${describeNode(value)})`,
    value: encodePath(path),
  };

  const children = Array.isArray(value)
    ? value.flatMap((nestedValue, index) => buildCopyTargets(nestedValue, [...path, index]))
    : Object.keys(value).flatMap((key) => buildCopyTargets(value[key], [...path, key]));

  return [current, ...children];
};

const getValueAtPath = (value, path = []) => {
  if (!path.length) return value;

  return path.reduce((acc, key) => {
    if (acc === undefined || acc === null) {
      return undefined;
    }

    return acc[key];
  }, value);
};

const copyTextToClipboard = async (text) => {
  if (typeof navigator !== 'undefined' && navigator?.clipboard?.writeText) {
    await navigator.clipboard.writeText(text);
    return true;
  }

  if (typeof document === 'undefined') {
    return false;
  }

  const textarea = document.createElement('textarea');
  textarea.value = text;
  textarea.setAttribute('readonly', '');
  textarea.style.position = 'absolute';
  textarea.style.left = '-9999px';
  document.body.appendChild(textarea);
  textarea.select();

  try {
    document.execCommand('copy');
    document.body.removeChild(textarea);
    return true;
  } catch (e) {
    document.body.removeChild(textarea);
    return false;
  }
};

const { Option } = Select;

const filterOperators = [
  { label: 'Equal', value: 'eq' },
  { label: 'Not equal', value: 'ne' },
  { label: 'Greater than', value: 'gt' },
  { label: 'Greater than equal', value: 'gte' },
  { label: 'Less than', value: 'lt' },
  { label: 'Less than equal', value: 'lte' },
];

export default function DataSampleTool() {
  const apiGateway = createApiGateway();
  const { effective: effectiveTheme } = useThemeContext();
  const jsonBaseStyles = effectiveTheme === 'dark' ? darkStyles : defaultStyles;

  const [jsonToShow, setJsonToShow] = useState(null);
  const [loading, setLoading] = useState(true);
  const [ssps, setSsps] = useState([]);
  const [dsps, setDsps] = useState([]);
  const [samples, setSamples] = useState([]);
  const [copyTargets, setCopyTargets] = useState([]);
  const [selectedCopyTarget, setSelectedCopyTarget] = useState('');
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    (async () => {
      // eslint-disable-next-line @typescript-eslint/no-shadow
      const [sspList, dspList, samples] = await Promise.all([
        apiGateway.ssps.getAll({ includeSpend: false }),
        apiGateway.dsps.getAll({ includeSpend: false }),
        apiGateway.samples.list(),
      ]);

      setSamples(samples);
      setSsps(sspList.filter((x) => x.isActive));
      setDsps(dspList.filter((x) => x.isActive));

      setLoading(false);
    })();
  }, []);

  useEffect(() => {
    if (!jsonToShow) {
      setCopyTargets([]);
      setSelectedCopyTarget('');
      return;
    }

    const targets = buildCopyTargets(jsonToShow);
    setCopyTargets(targets);
    setSelectedCopyTarget(targets[0]?.value || '');
  }, [jsonToShow]);

  const onFinish = async (values) => {
    try {
      setLoading(true);

      if (values.filters) {
        values.filters = values.filters.map(({ field, operator, value }) => ({
          field,
          value: toNumber(value) || (value == 0 ? 0 : value),
          operator,
        }));
      } else {
        values.filters = [];
      }

      await apiGateway.statistics.getReqOrResStatistics(omitBy(values, isNil));

      notification.info({
        title: 'Sample will be available soon',
      });

      setLoading(false);
    } catch (e) {
      setLoading(false);

      notification.error({
        title: 'Error fetching data. Please refresh the page and try again',
        description: e.isAxiosError ? JSON.stringify(e.response.data) : e.message,
      });
    }
  };

  const refreshSamples = async () => {
    try {
      setRefreshing(true);

      const list = await apiGateway.samples.list();

      setSamples(list);
    } catch (e) {
      notification.error({
        title: 'Failed to refresh samples',
        description: e.isAxiosError ? JSON.stringify(e.response.data) : e.message,
      });
    } finally {
      setRefreshing(false);
    }
  };

  const clearJsonToShow = () => {
    setJsonToShow(null);
  };

  const copyNodeToClipboard = async (path, successMessage) => {
    if (!jsonToShow) return;

    const nodeValue = getValueAtPath(jsonToShow, path);

    if (typeof nodeValue === 'undefined') {
      notification.error({ title: 'Nothing to copy for the selected path' });
      return;
    }

    const formattedValue =
      nodeValue !== null && typeof nodeValue === 'object' ? JSON.stringify(nodeValue, null, 2) : String(nodeValue);

    try {
      const copied = await copyTextToClipboard(formattedValue);

      if (copied) {
        notification.success({ title: successMessage });
      } else {
        notification.error({ title: 'Unable to copy to clipboard' });
      }
    } catch (e) {
      notification.error({ title: 'Unable to copy to clipboard' });
    }
  };

  const handleCopyEntireJson = () => copyNodeToClipboard([], 'Entire JSON copied to clipboard');

  const handleCopySelectedNode = () => {
    if (!selectedCopyTarget) return;

    copyNodeToClipboard(decodePath(selectedCopyTarget), 'Selected fragment copied to clipboard');
  };

  const getJsonWindow = () => (
    <Modal
      title="JSON Object"
      open
      width="100%"
      styles={{ body: { overflowX: 'scroll' } }}
      style={{ top: 20 }}
      onCancel={clearJsonToShow}
      onOk={clearJsonToShow}
    >
      <Space className="xchange-filters" style={{ marginBottom: 16 }} wrap>
        <Button icon={<CopyOutlined />} onClick={handleCopyEntireJson}>
          Copy entire JSON
        </Button>

        {copyTargets.length ? (
          <>
            <Select
              showSearch
              placeholder="Select object/array"
              optionFilterProp="label"
              style={{ minWidth: 280 }}
              value={selectedCopyTarget || undefined}
              onChange={(value) => setSelectedCopyTarget(value)}
              options={copyTargets}
            />
            <Button
              icon={<CopyOutlined />}
              onClick={handleCopySelectedNode}
              disabled={!selectedCopyTarget}
              type="default"
            >
              Copy selected
            </Button>
          </>
        ) : null}
      </Space>
      <JsonView
        data={jsonToShow}
        style={{
          ...jsonBaseStyles,
          noQuotesForStringValues: false,
          quotesForFieldNames: true,
        }}
      />
    </Modal>
  );

  const selectSearch = (inputValue, option) => {
    const searchValue = JSON.stringify(option.children);

    return searchValue.toLowerCase().trim().includes(inputValue.toLowerCase().trim());
  };

  const [form] = Form.useForm();

  if (loading || !form) return <Spinner loading />;

  return (
    <div className="xchange-page">
      <Card title="Data Sample" size="small" variant="outlined" style={{ marginTop: 16 }}>
        <Form onFinish={onFinish} form={form}>
          <Form.Item
            name="type"
            label="Event"
            initialValue="sspBidRequest"
            rules={[{ required: true, message: 'Please select the event' }]}
          >
            <Select style={{ width: '100%', maxWidth: 300 }}>
              <Option value="fullFlow">Full Flow</Option>
              <Option value="sspBidRequest">SSP Bid Request</Option>
              <Option value="dspBidRequest">DSP Bid Request</Option>
              <Option value="dspBidResponse">DSP Bid Response</Option>
            </Select>
          </Form.Item>

          <Form.Item
            name="sspId"
            label="SSP"
            initialValue="any"
            rules={[{ required: true, message: 'Please select SSP' }]}
          >
            <Select style={{ width: '100%', maxWidth: 300 }} showSearch allowClear filterOption={selectSearch}>
              <Option value="any">Random</Option>
              <Option value="unknown">Unknown (Only for SSP Bid Request event)</Option>
              {ssps.map((x, i) => (
                <Option key={i} value={x.id}>
                  {x.id} {x.name}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            name="dspId"
            label="DSP"
            initialValue="any"
            rules={[{ required: true, message: 'Please select DSP' }]}
          >
            <Select style={{ width: '100%', maxWidth: 300 }} showSearch allowClear filterOption={selectSearch}>
              <Option value="any">Random</Option>
              {dsps.map((x, i) => (
                <Option key={i} value={x.id}>
                  {x.id} {x.name}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            name="timeoutMs"
            label="Timeout"
            initialValue={10_000}
            rules={[{ required: true, message: 'Please select the timeout' }]}
          >
            <Select
              style={{ width: '100%', maxWidth: 300 }}
              options={[
                { value: 5_000, label: '5 seconds' },
                { value: 10_000, label: '10 seconds' },
                { value: 30_000, label: '30 seconds' },
                { value: 60_000, label: '1 minute' },
                { value: 60_000 * 5, label: '5 minutes' },
                { value: 60_000 * 10, label: '10 minutes' },
                { value: 60_000 * 15, label: '15 minutes' },
                { value: 60_000 * 30, label: '30 minutes' },
                { value: 60_000 * 60, label: '1 hour' },
              ]}
            />
          </Form.Item>

          <Form.List name="filters">
            {(fields, { add, remove }) => (
              <>
                {fields.map(({ key, name, ...restField }) => (
                  <Space
                    key={key}
                    className="xchange-filters"
                    wrap
                    style={{
                      display: 'flex',
                      marginBottom: 8,
                    }}
                    align="baseline"
                  >
                    <Form.Item
                      {...restField}
                      name={[name, 'field']}
                      label="Filter By (Field Path Here)"
                      style={{ minWidth: 300 }}
                    >
                      <Input placeholder="imp[0].bidfloor" />
                    </Form.Item>

                    <Form.Item {...restField} name={[name, 'operator']} label="Operator" style={{ minWidth: 300 }}>
                      <Select options={filterOperators} />
                    </Form.Item>

                    <Form.Item {...restField} name={[name, 'value']} label="Filter Value" style={{ minWidth: 300 }}>
                      <Input />
                    </Form.Item>

                    <MinusCircleOutlined onClick={() => remove(name)} />
                  </Space>
                ))}
                <Form.Item>
                  <Button type="dashed" onClick={() => add()} block icon={<PlusOutlined />}>
                    Add filter
                  </Button>
                </Form.Item>
              </>
            )}
          </Form.List>

          <Form.Item>
            <Button type="primary" htmlType="submit">
              Get Data Sample
            </Button>
          </Form.Item>
        </Form>

        <Space className="xchange-filters" style={{ marginBottom: 16 }} wrap>
          <Button icon={<ReloadOutlined />} onClick={refreshSamples} loading={refreshing}>
            Refresh
          </Button>
        </Space>

        <Table
          pagination={false}
          size="small"
          scroll={{ x: 'max-content' }}
          dataSource={_.orderBy(samples, (x) => x.key.replace('sample:', ''), 'desc')}
          columns={[
            {
              title: 'Date Time',
              dataIndex: 'key',
              key: 'key',
              defaultSortOrder: 'descend',
              render: (value) => moment(value.replace('sample:', '')).format('YYYY-MM-DD HH:mm:SS'),
              sorter: (a, b) => moment(a.key.replace('sample:', '')) - moment(b.key.replace('sample:', '')),
            },
            {
              title: 'SSP',
              dataIndex: 'sspId',
              key: 'sspId',
              render: (value, record) => {
                const { sspId } = record.value.meta;

                if (!sspId) return '-';

                const ssp = ssps.find((x) => x.id === record.value.meta.sspId);

                return `[${sspId}] ${ssp.name}`;
              },
            },
            {
              title: 'DSP',
              dataIndex: 'dspId',
              key: 'dspId',
              render: (value, record) => {
                const { dspId } = record.value.meta;

                if (!dspId) return '-';

                const dsp = dsps.find((x) => x.id === record.value.meta.dspId);

                return `[${dspId}] ${dsp.name}`;
              },
            },
            {
              title: 'Sample',
              dataIndex: 'value',
              key: 'value',
              render: (value) => (
                <Button type="text" onClick={() => setJsonToShow(value)}>
                  Open Sample
                </Button>
              ),
            },
          ]}
        />

        {jsonToShow ? getJsonWindow() : ''}
      </Card>
    </div>
  );
}
